import { el, clear } from "@utils/dom";
import { getState } from "@state/store";
import type { Table, Seat, SeatingChart, ProximityType } from "../../types/models";
import { getTableSize, calculateSeatPositions } from "./SeatingChartEditor";
import { errorToast } from "./Toasts";

// Theme-aware table colors
const TABLE_COLORS_LIGHT = [
    "#e8f4f8", "#f8e8f4", "#f4f8e8", "#f8f4e8",
    "#e8e8f8", "#f8e8e8", "#e8f8e8", "#f4e8f8"
];

const TABLE_COLORS_DARK = [
    "#1a3a4a", "#3a1a3a", "#2a3a1a", "#3a3a1a",
    "#2a2a4a", "#3a2a2a", "#1a3a2a", "#3a1a4a"
];

function getTableColors(): string[] {
    const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return isDark ? TABLE_COLORS_DARK : TABLE_COLORS_LIGHT;
}

export interface RadiusSelectorResult {
    anchorSeatId: string;
    radius: number;
}

/**
 * Open a visual radius selector dialog
 * Returns the selected anchor seat and radius, or null if cancelled
 */
export function openRadiusSelector(
    type: ProximityType,
    onComplete: (result: RadiusSelectorResult | null) => void
): void {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) {
        onComplete(null);
        return;
    }

    const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId)
        ?? cls.seatingCharts?.[0];

    if (!chart || chart.tables.length === 0) {
        errorToast("Create a seating chart with tables first");
        onComplete(null);
        return;
    }

    // Find first seat to use as default anchor
    const firstTable = chart.tables[0];
    const firstSeat = firstTable?.seats[0];
    if (!firstTable || !firstSeat) {
        errorToast("Add seats to your tables first");
        onComplete(null);
        return;
    }

    // Capture chart for use in closures
    const activeChart = chart;

    // State for the selector
    let anchorSeatId = firstSeat.id;
    let anchorTableId = firstTable.id;
    let currentRadius = 100; // Default radius in pixels
    let isDragging = false;

    // Create modal backdrop
    const backdrop = el("div", {
        class: "radius-selector-backdrop",
        style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); z-index: 1000; display: flex; align-items: center; justify-content: center;"
    });

    // Create dialog
    const dialog = el("div", {
        class: "radius-selector-dialog",
        style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 700px; width: 95%; max-height: 90vh; overflow: auto; border: 1px solid var(--border);"
    });

    const title = el("h3", { style: "margin: 0 0 8px 0;" },
        type === 'within' ? "Set Keep Within Radius" : "Set Keep Apart Radius"
    );

    const instructions = el("p", {
        style: "margin: 0 0 16px 0; font-size: 0.875rem; color: var(--muted);"
    }, type === 'within'
        ? "Click a seat to select it as the anchor. Drag the circle edge to set the radius. Students must sit within this distance."
        : "Click a seat to select it as the anchor. Drag the circle edge to set the radius. Students must sit outside this distance."
    );

    // Radius display (show pixel radius)
    const radiusDisplay = el("div", {
        style: "text-align: center; margin-bottom: 16px; font-size: 1.25rem; font-weight: 600;"
    }, `Radius: ${currentRadius}px`);

    // Chart container
    const chartContainer = el("div", {
        class: "radius-chart-container",
        style: "position: relative; min-height: 350px; background: var(--bg); border: 1px solid var(--border); border-radius: 12px; overflow: hidden;"
    });

    // SVG overlay for the radius circle
    const svgOverlay = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svgOverlay.setAttribute("style", "position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 50;");

    const radiusCircle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    radiusCircle.setAttribute("fill", type === 'within' ? "rgba(39, 174, 96, 0.15)" : "rgba(231, 76, 60, 0.15)");
    radiusCircle.setAttribute("stroke", type === 'within' ? "#27ae60" : "#e74c3c");
    radiusCircle.setAttribute("stroke-width", "3");
    radiusCircle.setAttribute("stroke-dasharray", "8,4");

    const dragHandle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    dragHandle.setAttribute("r", "12");
    dragHandle.setAttribute("fill", type === 'within' ? "#27ae60" : "#e74c3c");
    dragHandle.setAttribute("stroke", "white");
    dragHandle.setAttribute("stroke-width", "3");
    dragHandle.setAttribute("style", "cursor: grab; pointer-events: all;");

    svgOverlay.appendChild(radiusCircle);
    svgOverlay.appendChild(dragHandle);

    function getAnchorPosition(): { x: number; y: number; table: Table } | null {
        const table = activeChart.tables.find(t => t.id === anchorTableId);
        if (!table) return null;

        const seatIndex = table.seats.findIndex(s => s.id === anchorSeatId);
        if (seatIndex === -1) return null;

        const size = getTableSize(table);
        const positions = calculateSeatPositions(table.shape, table.seats, size);
        const pos = positions[seatIndex];

        if (!pos) return null;

        return {
            x: table.x + pos.x,
            y: table.y + pos.y,
            table
        };
    }

    function updateRadiusCircle() {
        const anchor = getAnchorPosition();
        if (!anchor) return;

        radiusCircle.setAttribute("cx", String(anchor.x));
        radiusCircle.setAttribute("cy", String(anchor.y));
        radiusCircle.setAttribute("r", String(currentRadius));

        // Position drag handle at right edge of circle
        dragHandle.setAttribute("cx", String(anchor.x + currentRadius));
        dragHandle.setAttribute("cy", String(anchor.y));

        radiusDisplay.textContent = `Radius: ${currentRadius}px`;

        // Highlight seats within/outside radius
        highlightSeats();
    }

    function highlightSeats() {
        const anchor = getAnchorPosition();
        if (!anchor) return;

        // Reset all seat highlights
        chartContainer.querySelectorAll('.radius-seat').forEach(seatEl => {
            seatEl.classList.remove('in-radius', 'out-radius', 'anchor');
        });

        // Mark anchor seat
        const anchorEl = chartContainer.querySelector(`[data-seat-id="${anchorSeatId}"]`);
        if (anchorEl) anchorEl.classList.add('anchor');

        // Check ALL seats on ALL tables using pixel distance
        for (const table of activeChart.tables) {
            const size = getTableSize(table);
            const positions = calculateSeatPositions(table.shape, table.seats, size);

            for (let i = 0; i < table.seats.length; i++) {
                const seat = table.seats[i];
                if (!seat || seat.id === anchorSeatId) continue;

                const pos = positions[i];
                if (!pos) continue;

                // Calculate absolute position of this seat
                const seatX = table.x + pos.x;
                const seatY = table.y + pos.y;

                // Calculate pixel distance from anchor
                const dx = seatX - anchor.x;
                const dy = seatY - anchor.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                const seatEl = chartContainer.querySelector(`[data-seat-id="${seat.id}"]`);
                if (seatEl) {
                    if (distance <= currentRadius) {
                        seatEl.classList.add('in-radius');
                    } else {
                        seatEl.classList.add('out-radius');
                    }
                }
            }
        }
    }

    function renderChart() {
        // Clear existing content except SVG
        const existingTables = chartContainer.querySelectorAll('.seating-table');
        existingTables.forEach(t => t.remove());

        const tableColors = getTableColors();

        for (const table of activeChart.tables) {
            const size = getTableSize(table);
            const colorIndex = parseInt(table.id.slice(-2), 36) % tableColors.length;
            const bgColor = tableColors[colorIndex];

            const tableEl = el("div", {
                class: `seating-table ${table.shape}`,
                style: `
                    position: absolute;
                    left: ${table.x}px;
                    top: ${table.y}px;
                    width: ${size.width}px;
                    height: ${size.height}px;
                    background: ${bgColor};
                    border: 2px solid var(--border);
                    border-radius: ${table.shape === 'round' ? '50%' : '8px'};
                `
            });

            // Table name
            tableEl.appendChild(
                el("div", {
                    style: "position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-weight: 600; font-size: 11px; color: var(--muted);"
                }, table.name)
            );

            // Render seats
            const positions = calculateSeatPositions(table.shape, table.seats, size);

            for (let i = 0; i < table.seats.length; i++) {
                const seat = table.seats[i];
                const pos = positions[i];

                if (!seat || !pos) continue;

                const isAnchor = seat.id === anchorSeatId;

                const seatEl = el("div", {
                    class: `radius-seat ${isAnchor ? 'anchor' : ''}`,
                    "data-seat-id": seat.id,
                    "data-table-id": table.id,
                    style: `
                        position: absolute;
                        left: ${pos.x - 18}px;
                        top: ${pos.y - 18}px;
                        width: 36px;
                        height: 36px;
                        border-radius: 50%;
                        background: var(--card);
                        border: 2px solid var(--border);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 10px;
                        color: var(--muted);
                        cursor: pointer;
                        transition: all 0.15s ease;
                        z-index: 10;
                    `
                }, String(i + 1));

                // Click to select as anchor
                seatEl.addEventListener("click", () => {
                    anchorSeatId = seat.id;
                    anchorTableId = table.id;
                    updateRadiusCircle();
                });

                tableEl.appendChild(seatEl);
            }

            chartContainer.appendChild(tableEl);
        }

        chartContainer.appendChild(svgOverlay);
        updateRadiusCircle();
    }

    // Drag handling for the radius knob
    dragHandle.addEventListener("mousedown", (e: MouseEvent) => {
        e.preventDefault();
        isDragging = true;
        dragHandle.setAttribute("style", "cursor: grabbing; pointer-events: all;");
    });

    document.addEventListener("mousemove", (e: MouseEvent) => {
        if (!isDragging) return;

        const anchor = getAnchorPosition();
        if (!anchor) return;

        const rect = chartContainer.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Calculate distance from anchor to mouse (direct pixel distance)
        const dx = mouseX - anchor.x;
        const dy = mouseY - anchor.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Use direct pixel radius (min 20px, max 500px)
        currentRadius = Math.max(20, Math.min(500, Math.round(distance)));

        updateRadiusCircle();
    });

    document.addEventListener("mouseup", () => {
        if (isDragging) {
            isDragging = false;
            dragHandle.setAttribute("style", "cursor: grab; pointer-events: all;");
        }
    });

    // Buttons
    const buttons = el("div", {
        style: "display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px;"
    });

    const cancelBtn = el("button", {
        style: "padding: 8px 16px;"
    }, "Cancel");

    cancelBtn.addEventListener("click", () => {
        document.body.removeChild(backdrop);
        onComplete(null);
    });

    const confirmBtn = el("button", {
        class: "primary",
        style: "padding: 8px 16px;"
    }, "Confirm Radius");

    confirmBtn.addEventListener("click", () => {
        document.body.removeChild(backdrop);
        onComplete({
            anchorSeatId,
            radius: currentRadius  // Return pixel radius directly
        });
    });

    buttons.append(cancelBtn, confirmBtn);

    dialog.append(title, instructions, radiusDisplay, chartContainer, buttons);
    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    // Close on backdrop click
    backdrop.addEventListener("click", (e) => {
        if (e.target === backdrop) {
            document.body.removeChild(backdrop);
            onComplete(null);
        }
    });

    // Initial render
    renderChart();
}
